/**
 * Google "Preferred Sources" integration.
 * Docs: https://developers.google.com/search/docs/appearance/preferred-sources
 *
 * We use the advanced (manual) integration so the trigger matches the site design
 * instead of injecting Google's own badge. If the library fails to load
 * (ad-blockers, CSP, offline) we gracefully fall back to the deeplink.
 */

const SCRIPT_SRC = "https://news.google.com/swg/js/v1/publisher.js";
const DOMAIN = "ofix.co.il";
export const PREFERRED_SOURCE_DEEPLINK = `https://www.google.com/preferences/source?q=${DOMAIN}`;

export const PS_DISMISSED_KEY = "ofix_preferred_source_dismissed_v1";
export const PS_DONE_KEY = "ofix_preferred_source_clicked_v1";

declare global {
  interface Window {
    PREFERRED_SOURCE?: any[];
  }
}

let loading = false;

/** Loads Google's publisher.js once, in manual mode (no auto-rendered badge). */
export const loadPreferredSourceLib = () => {
  if (typeof document === "undefined" || loading) return;
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  loading = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = SCRIPT_SRC;
  s.setAttribute("preferred-sources-control", "manual");
  document.head.appendChild(s);

  window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
  window.PREFERRED_SOURCE.push((preferredSource: any) => {
    try {
      preferredSource.init({ theme: "light", lang: "he" });
    } catch {
      /* noop */
    }
  });
};

/** Opens the Google flow; falls back to the deeplink when the SDK isn't available. */
export const openPreferredSourceFlow = () => {
  markPreferredSourceDone();
  const queue = window.PREFERRED_SOURCE;
  const api = Array.isArray(queue) ? (queue as any).addPreferredSource : undefined;
  if (typeof api === "function") {
    try {
      api.call(queue);
      return;
    } catch {
      /* fall through to deeplink */
    }
  }
  window.open(PREFERRED_SOURCE_DEEPLINK, "_blank", "noopener,noreferrer");
};

export const isPreferredSourceDone = () => {
  try {
    return localStorage.getItem(PS_DONE_KEY) === "1";
  } catch {
    return false;
  }
};

export const markPreferredSourceDone = () => {
  try {
    localStorage.setItem(PS_DONE_KEY, "1");
  } catch {
    /* noop */
  }
};

/** Nudge is snoozed for 30 days after a dismiss. */
export const isNudgeSnoozed = () => {
  try {
    const raw = localStorage.getItem(PS_DISMISSED_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return true;
    return Date.now() - ts < 1000 * 60 * 60 * 24 * 30;
  } catch {
    return false;
  }
};

export const snoozeNudge = () => {
  try {
    localStorage.setItem(PS_DISMISSED_KEY, String(Date.now()));
  } catch {
    /* noop */
  }
};
